import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI, inventoryAPI, floorPlanAPI, leadAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, Building2, MapPin, Home, IndianRupee, Save,
  Layers, Calendar, Users, FileText, Globe, Award, Edit,
  CheckCircle, XCircle, Lock, Image, Calculator, Settings,
  Plus, Compass, Ruler, Phone, MessageSquare, Bot, TrendingUp,
  Clock, UserCheck, ExternalLink, Eye
} from 'lucide-react';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [project, setProject] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [floorPlans, setFloorPlans] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [formData, setFormData] = useState({});
  const [pricingData, setPricingData] = useState({
    base_price_per_sqft: '',
    floor_rise: '',
    parking_cost: '',
    clubhouse_cost: '',
    development_charges: '',
    other_charges: '',
  });

  const isBuilder = hasRole(['builder_admin']);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [projectRes, inventoryRes, floorPlansRes, leadsRes] = await Promise.all([
        projectAPI.get(id),
        inventoryAPI.list({ project_id: id }).catch(() => ({ data: [] })),
        floorPlanAPI.list({ project_id: id }).catch(() => ({ data: [] })),
        leadAPI.list({ project_id: id }).catch(() => ({ data: { leads: [] } })),
      ]);
      
      setProject(projectRes.data);
      setFormData(projectRes.data);
      setInventory(inventoryRes.data || []);
      setFloorPlans(floorPlansRes.data || []);
      setLeads(leadsRes.data?.leads || leadsRes.data || []);
      
      // Extract pricing from first inventory unit if exists
      if (inventoryRes.data?.length > 0) {
        const firstUnit = inventoryRes.data[0];
        setPricingData({
          base_price_per_sqft: firstUnit.base_price || '',
          floor_rise: firstUnit.floor_rise || '',
          parking_cost: firstUnit.parking_cost || '',
          clubhouse_cost: firstUnit.clubhouse_cost || '',
          development_charges: firstUnit.development_charges || '',
          other_charges: firstUnit.other_charges || '',
        });
      }
    } catch (error) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await projectAPI.update(id, formData);
      setProject(formData);
      setEditing(false);
      toast.success('Project updated successfully');
    } catch (error) {
      toast.error('Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const stats = {
    totalUnits: inventory.length,
    available: inventory.filter(u => u.status === 'available').length,
    sold: inventory.filter(u => u.status === 'sold').length,
    blocked: inventory.filter(u => u.status === 'blocked').length,
  };

  // Lead stats
  const leadStats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    siteVisit: leads.filter(l => l.status === 'site_visit_scheduled' || l.status === 'site_visit_completed').length,
    booked: leads.filter(l => l.status === 'booked').length,
  };

  // Group inventory by floor
  const floorGroups = inventory.reduce((acc, unit) => {
    const floor = unit.floor || 0;
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(unit);
    return acc;
  }, {});

  // Group by BHK type
  const bhkGroups = inventory.reduce((acc, unit) => {
    const bhk = unit.bhk_type || 'Unknown';
    if (!acc[bhk]) acc[bhk] = { count: 0, available: 0, price_range: [] };
    acc[bhk].count++;
    if (unit.status === 'available') acc[bhk].available++;
    if (unit.price) acc[bhk].price_range.push(unit.price);
    return acc;
  }, {});

  const formatPrice = (price) => {
    if (!price) return '-';
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p>Project not found</p>
        <Button variant="link" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{project.name}</h1>
            <p className="text-muted-foreground flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {project.location}, {project.city}
            </p>
          </div>
        </div>
        {isBuilder && !editing && (
          <Button onClick={() => setEditing(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Project
          </Button>
        )}
        {isBuilder && editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditing(false); setFormData(project); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Layers className="h-4 w-4" />
              <span className="text-sm">Total Units</span>
            </div>
            <p className="text-2xl font-bold">{stats.totalUnits}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">Available</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{stats.available}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <XCircle className="h-4 w-4" />
              <span className="text-sm">Sold</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{stats.sold}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Lock className="h-4 w-4" />
              <span className="text-sm">Blocked</span>
            </div>
            <p className="text-2xl font-bold">{stats.blocked}</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate(`/builder/crm?project=${id}`)}>
          <Users className="h-4 w-4 mr-2" />
          View Leads ({leadStats.total})
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/builder/inventory?project=${id}`)}>
          <Layers className="h-4 w-4 mr-2" />
          Manage Inventory
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/builder/floor-plans?project=${id}`)}>
          <Image className="h-4 w-4 mr-2" />
          Floor Plans
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/builder/site-visits?project=${id}`)}>
          <Calendar className="h-4 w-4 mr-2" />
          Site Visits ({leadStats.siteVisit})
        </Button>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="overview" className="flex items-center gap-1 text-xs">
            <Building2 className="h-3 w-3" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="leads" className="flex items-center gap-1 text-xs">
            <Users className="h-3 w-3" />
            Leads
          </TabsTrigger>
          <TabsTrigger value="inventory" className="flex items-center gap-1 text-xs">
            <Layers className="h-3 w-3" />
            Inventory
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-1 text-xs">
            <Calculator className="h-3 w-3" />
            Pricing
          </TabsTrigger>
          <TabsTrigger value="floorplans" className="flex items-center gap-1 text-xs">
            <Image className="h-3 w-3" />
            Floor Plans
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-1 text-xs">
            <Settings className="h-3 w-3" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Project Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="space-y-2">
                      <Label>Project Name</Label>
                      <Input 
                        value={formData.name || ''} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea 
                        value={formData.description || ''} 
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input 
                          value={formData.location || ''} 
                          onChange={(e) => setFormData({...formData, location: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input 
                          value={formData.city || ''} 
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Description</p>
                      <p>{project.description || 'No description'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Location</p>
                        <p className="font-medium">{project.location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">City</p>
                        <p className="font-medium">{project.city}, {project.state}</p>
                      </div>
                    </div>
                    {project.rera_number && (
                      <div>
                        <p className="text-sm text-muted-foreground">RERA Number</p>
                        <p className="font-medium flex items-center gap-2">
                          <Award className="h-4 w-4 text-green-600" />
                          {project.rera_number}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* BHK Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Unit Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(bhkGroups).length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No units added yet</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(bhkGroups).map(([bhk, data]) => (
                      <div key={bhk} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Home className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-semibold">{bhk}</p>
                            <p className="text-sm text-muted-foreground">
                              {data.available} available / {data.count} total
                            </p>
                          </div>
                        </div>
                        {data.price_range.length > 0 && (
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">From</p>
                            <p className="font-semibold text-green-600">
                              {formatPrice(Math.min(...data.price_range))}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Amenities */}
          {project.amenities && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Amenities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(project.amenities) 
                    ? project.amenities 
                    : (typeof project.amenities === 'string' ? project.amenities.split(',') : [])
                  ).map((amenity, i) => (
                    <Badge key={i} variant="secondary" className="px-3 py-1">
                      {typeof amenity === 'string' ? amenity.trim() : amenity}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* LEADS TAB */}
        <TabsContent value="leads" className="space-y-4">
          {/* Lead Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{leadStats.total}</p>
                <p className="text-xs text-blue-600">Total Leads</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-yellow-700">{leadStats.new}</p>
                <p className="text-xs text-yellow-600">New</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-purple-700">{leadStats.contacted}</p>
                <p className="text-xs text-purple-600">Contacted</p>
              </CardContent>
            </Card>
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-orange-700">{leadStats.siteVisit}</p>
                <p className="text-xs text-orange-600">Site Visits</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{leadStats.booked}</p>
                <p className="text-xs text-green-600">Booked</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Leads */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Leads</CardTitle>
                <Button size="sm" onClick={() => navigate(`/builder/crm?project=${id}`)}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View All in CRM
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {leads.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No leads for this project yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leads.slice(0, 10).map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {lead.name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{lead.name}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            {lead.phone}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="mb-1">
                          {lead.status?.replace('_', ' ')}
                        </Badge>
                        {lead.bhk_interest && (
                          <p className="text-xs text-muted-foreground">{lead.bhk_interest}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Agent Placeholder */}
          <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bot className="h-5 w-5 text-violet-600" />
                AI Call Agent
                <Badge className="bg-violet-500">Coming Soon</Badge>
              </CardTitle>
              <CardDescription>
                Automated post-call CRM updates and lead disposition tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-3 bg-white/80 rounded-lg">
                  <Phone className="h-6 w-6 text-violet-500 mb-2" />
                  <p className="font-medium">Auto Call Logging</p>
                  <p className="text-sm text-muted-foreground">AI listens to calls and logs disposition</p>
                </div>
                <div className="p-3 bg-white/80 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-violet-500 mb-2" />
                  <p className="font-medium">Conversation Summary</p>
                  <p className="text-sm text-muted-foreground">Get key points from each call</p>
                </div>
                <div className="p-3 bg-white/80 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-violet-500 mb-2" />
                  <p className="font-medium">Smart Follow-ups</p>
                  <p className="text-sm text-muted-foreground">AI suggests next best action</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* INVENTORY TAB */}
        <TabsContent value="inventory" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Inventory by Floor</h3>
            {isBuilder && (
              <Button onClick={() => navigate(`/builder/inventory?project=${id}`)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Units
              </Button>
            )}
          </div>
          
          {Object.keys(floorGroups).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No inventory added</p>
                <p className="text-muted-foreground">Add units to track availability</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.keys(floorGroups).sort((a, b) => b - a).map((floor) => (
                <Card key={floor}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Floor {floor}
                      <Badge variant="outline">{floorGroups[floor].length} units</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {floorGroups[floor].map((unit) => (
                        <div
                          key={unit.id}
                          className={`px-3 py-2 rounded-lg text-sm ${
                            unit.status === 'available'
                              ? 'bg-emerald-100 text-emerald-700'
                              : unit.status === 'blocked'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          <span className="font-semibold">{unit.unit_number}</span>
                          <span className="text-xs ml-1">• {unit.bhk_type}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PRICING TAB */}
        <TabsContent value="pricing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Pricing Configuration
              </CardTitle>
              <CardDescription>
                Set base pricing and additional charges for this project
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Base Pricing */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-4">
                <h4 className="font-semibold">Base Pricing</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Base Price (₹/sq.ft)</Label>
                    <Input 
                      type="number"
                      value={pricingData.base_price_per_sqft}
                      onChange={(e) => setPricingData({...pricingData, base_price_per_sqft: e.target.value})}
                      placeholder="e.g., 8500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Floor Rise (₹/sq.ft/floor)</Label>
                    <Input 
                      type="number"
                      value={pricingData.floor_rise}
                      onChange={(e) => setPricingData({...pricingData, floor_rise: e.target.value})}
                      placeholder="e.g., 50"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Charges */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                <h4 className="font-semibold text-amber-800">Additional Charges</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Parking (₹)</Label>
                    <Input 
                      type="number"
                      value={pricingData.parking_cost}
                      onChange={(e) => setPricingData({...pricingData, parking_cost: e.target.value})}
                      placeholder="e.g., 500000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Clubhouse (₹)</Label>
                    <Input 
                      type="number"
                      value={pricingData.clubhouse_cost}
                      onChange={(e) => setPricingData({...pricingData, clubhouse_cost: e.target.value})}
                      placeholder="e.g., 100000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Development Charges (₹)</Label>
                    <Input 
                      type="number"
                      value={pricingData.development_charges}
                      onChange={(e) => setPricingData({...pricingData, development_charges: e.target.value})}
                      placeholder="e.g., 200000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Other Charges (₹)</Label>
                    <Input 
                      type="number"
                      value={pricingData.other_charges}
                      onChange={(e) => setPricingData({...pricingData, other_charges: e.target.value})}
                      placeholder="e.g., 50000"
                    />
                  </div>
                </div>
              </div>

              {/* Price Summary */}
              {pricingData.base_price_per_sqft && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-3">Sample Price Calculation</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {[1000, 1200, 1500].map(area => {
                      const basePrice = Number(pricingData.base_price_per_sqft) || 0;
                      const saleableArea = area * 1.25;
                      const unitPrice = saleableArea * basePrice;
                      const parking = Number(pricingData.parking_cost) || 0;
                      const club = Number(pricingData.clubhouse_cost) || 0;
                      const dev = Number(pricingData.development_charges) || 0;
                      const other = Number(pricingData.other_charges) || 0;
                      const totalPrice = unitPrice + parking + club + dev + other;
                      
                      return (
                        <div key={area} className="p-3 bg-white rounded-lg">
                          <p className="text-muted-foreground">{area} sq.ft (carpet)</p>
                          <p className="text-lg font-bold text-green-600">{formatPrice(totalPrice)}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isBuilder && (
                <Button className="w-full">
                  <Save className="h-4 w-4 mr-2" />
                  Save Pricing Configuration
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FLOOR PLANS TAB */}
        <TabsContent value="floorplans" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Floor Plans</h3>
            {isBuilder && (
              <Button onClick={() => navigate(`/builder/floor-plans?project=${id}`)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Floor Plan
              </Button>
            )}
          </div>

          {floorPlans.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No floor plans added</p>
                <p className="text-muted-foreground">Upload floor plan images with unit mapping</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {floorPlans.map((fp) => (
                <Card 
                  key={fp.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/builder/floor-plans/${fp.id}`)}
                >
                  <CardContent className="p-4">
                    {fp.image_url && (
                      <img 
                        src={fp.image_url} 
                        alt={fp.name}
                        className="w-full h-40 object-cover rounded-lg mb-3"
                      />
                    )}
                    <h4 className="font-semibold">{fp.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {fp.tower && `${fp.tower} • `}
                      {fp.units?.length || 0} units mapped
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Project Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>RERA Number</Label>
                  <Input 
                    value={formData.rera_number || ''} 
                    onChange={(e) => setFormData({...formData, rera_number: e.target.value})}
                    disabled={!editing}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Project Slug</Label>
                  <Input 
                    value={project.slug || ''} 
                    disabled
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>BHK Options</Label>
                <Input 
                  value={formData.bhk_options || ''} 
                  onChange={(e) => setFormData({...formData, bhk_options: e.target.value})}
                  placeholder="e.g., 1 BHK, 2 BHK, 3 BHK"
                  disabled={!editing}
                />
              </div>

              <div className="space-y-2">
                <Label>Amenities (comma-separated)</Label>
                <Textarea 
                  value={formData.amenities || ''} 
                  onChange={(e) => setFormData({...formData, amenities: e.target.value})}
                  placeholder="e.g., Swimming Pool, Gym, Clubhouse, Garden"
                  disabled={!editing}
                  rows={2}
                />
              </div>

              <Alert>
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  Project ID: <code className="bg-muted px-1 rounded">{project.id}</code>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectDetailPage;
